import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { useAuth, isStaff } from "@/context/AuthContext";
import { api, openAuthedBlob, formatApiErrorDetail } from "@/lib/api";
import { StatusChip, formatDateShort, formatMoney, EmptyState } from "@/components/ventrix/atoms";
import Pagination from "@/components/ventrix/Pagination";
import { outstandingBalance, isFullyPaid, validatePaymentAmount } from "@/lib/invoice";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import StripeCheckout from "@/components/ventrix/StripeCheckout";
import { Download, CreditCard, FileText } from "lucide-react";

const LIMIT = 50;

export default function Invoices() {
  const { user } = useAuth();
  const staff = isStaff(user?.role);
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const load = () => {
    setItems(null);
    api.get(`/v1/invoices?limit=${LIMIT}&offset=${offset}`)
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total ?? (data.items || []).length); })
      .catch(() => { setItems([]); });
  };
  useEffect(() => { load(); }, [offset]); // eslint-disable-line

  // Finance/admin: confirm or reject a customer's pending bank transfer.
  const review = async (invoiceId, pid, action) => {
    try {
      await api.post(`/v1/invoices/${invoiceId}/payments/${pid}/${action}`);
      toast.success(action === "verify" ? "Payment verified" : "Payment rejected");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <PortalLayout section="customer">
      <div className="vtx-eyebrow">/ Invoices</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Invoices & payments</h1>

      <div className="vtx-card mt-6">
        {items === null && <div className="p-6 space-y-2">{[1,2,3].map(i=><div key={i} className="vtx-skeleton h-12" />)}</div>}
        {items && items.length === 0 && (
          <EmptyState icon={FileText} title="No invoices yet" hint="Invoices appear here once billing is issued for your shipments." />
        )}
        {items && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="vtx-table w-full">
              <thead><tr><th>Invoice #</th><th>Shipment</th><th>Amount</th><th>Due</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {items.map(i => {
                  const bal = outstandingBalance(i);
                  const paid = Number(i.amount_paid) || 0;
                  const pend = (i.payments || []).filter(p => p.verify_status === "pending");
                  const pendAmt = pend.reduce((s, p) => s + (Number(p.amount) || 0), 0);
                  const canPay = !isFullyPaid(i) && (paid + pendAmt) + 0.005 < Number(i.amount);
                  return (
                  <tr key={i.id} data-testid={`invoice-row-${i.invoice_number}`}>
                    <td className="font-mono font-semibold text-[#0E2F76]">{i.invoice_number}</td>
                    <td className="font-mono text-xs text-[#475569]">{i.shipment_id ? i.shipment_id.slice(-8) : "—"}</td>
                    <td className="font-semibold">
                      {formatMoney(i.amount, i.currency)}
                      {paid > 0 && !isFullyPaid(i) && (
                        <div className="text-[10px] font-normal text-[#475569]" data-testid={`invoice-balance-${i.invoice_number}`}>
                          Paid {formatMoney(paid, i.currency)} · Bal {formatMoney(bal, i.currency)}
                        </div>
                      )}
                    </td>
                    <td className="font-mono">{formatDateShort(i.due_date)}</td>
                    <td>
                      <StatusChip status={i.status} />
                      {pend.length > 0 && (
                        <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-[#B45309]" data-testid={`invoice-pending-${i.invoice_number}`}>
                          {formatMoney(pendAmt, i.currency)} pending verification
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button type="button"
                          onClick={() => openAuthedBlob(`/v1/invoices/${i.id}/pdf`).catch(() => toast.error("Could not open the invoice PDF."))}
                          className="inline-flex items-center gap-1 text-xs text-[#0E2F76] hover:text-[#d41f26] font-semibold" data-testid={`invoice-pdf-${i.invoice_number}`}>
                          <Download size={12} /> PDF
                        </button>
                        {canPay && <PayDialog invoice={i} onPaid={load} />}
                      </div>
                      {staff && pend.map(p => (
                        <div key={p.pid} className="mt-1.5 flex items-center gap-2 text-[11px]" data-testid={`pending-review-${i.invoice_number}`}>
                          <span className="text-[#475569] font-mono">{p.reference || "no ref"} · {formatMoney(Number(p.amount), i.currency)}</span>
                          <button className="text-[#166534] font-bold hover:underline" onClick={()=>review(i.id, p.pid, "verify")} data-testid={`verify-${i.invoice_number}`}>Verify</button>
                          <button className="text-[#d41f26] font-bold hover:underline" onClick={()=>review(i.id, p.pid, "reject")} data-testid={`reject-${i.invoice_number}`}>Reject</button>
                        </div>
                      ))}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
      </div>
    </PortalLayout>
  );
}

const FALLBACK_METHOD = { id: "manual", label: "Bank transfer", kind: "offline", reference_required: true, reference_label: "Bank transaction reference", note: "Bank transfers are reviewed by our team before the invoice is marked paid." };

function PayDialog({ invoice, onPaid }) {
  const balance = outstandingBalance(invoice);
  const [open, setOpen] = useState(false);
  const [methods, setMethods] = useState(null);
  const [method, setMethod] = useState("manual");
  const [reference, setReference] = useState("");
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState(String(balance));
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("form");        // "form" | "stripe"
  const [clientSecret, setClientSecret] = useState("");

  // Reset the amount to the current balance whenever the dialog opens, and load
  // the admin-enabled payment methods (only shown to customers when enabled).
  const onOpenChange = (o) => {
    setOpen(o);
    if (o) {
      setAmount(String(balance));
      setReference("");
      setPhone("");
      setStage("form");
      setClientSecret("");
      if (methods === null) {
        api.get("/v1/payments/methods")
          .then(({ data }) => {
            const list = data.methods || [];
            setMethods(list);
            if (list.length && !list.some((m) => m.id === method)) setMethod(list[0].id);
          })
          .catch(() => setMethods([FALLBACK_METHOD]));
      }
    }
  };

  const list = methods === null ? [FALLBACK_METHOD] : methods;
  const sel = list.find((m) => m.id === method) || (method === "manual" ? FALLBACK_METHOD : { id: method, kind: "offline", reference_required: false });
  const isStripe = method === "stripe";
  const isMpesaGateway = sel.kind === "gateway" && method === "mpesa";
  const isGateway = sel.kind === "gateway";
  const isOffline = !isGateway;
  const stripeMethod = (methods || []).find((m) => m.id === "stripe");

  const refRequired = isOffline && !!sel.reference_required;
  const refMissing = refRequired && !reference.trim();
  const phoneMissing = isMpesaGateway && !phone.trim();
  const error = isOffline ? validatePaymentAmount(amount, balance) : "";

  // Stripe: create a PaymentIntent for the outstanding balance, then hand off to
  // Stripe Elements. Settlement lands via the webhook, so we don't touch the
  // ledger here — we just confirm the card and tell the customer it's settling.
  const startStripe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/v1/payments/intent", { invoice_id: invoice.id, method: "stripe" });
      if (!data.intent?.client_secret) throw new Error("no client secret");
      setClientSecret(data.intent.client_secret);
      setStage("stripe");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start card payment"); }
    finally { setBusy(false); }
  };

  // M-Pesa STK push: initiate the gateway prompt for the full balance. Settlement
  // lands asynchronously via the Daraja callback, so we just tell the customer to
  // approve it on their phone and close — the invoice updates once confirmed.
  const startMpesa = async () => {
    if (phoneMissing) return;
    setBusy(true);
    try {
      await api.post("/v1/payments/intent", { invoice_id: invoice.id, method: "mpesa", phone: phone.trim() });
      toast.success(`M-Pesa prompt sent to ${phone.trim()} — approve it on your phone. Your invoice updates once M-Pesa confirms.`);
      setOpen(false); onPaid();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start the M-Pesa prompt."); }
    finally { setBusy(false); }
  };

  const onStripeSuccess = () => {
    toast.success("Payment received — it will settle on your invoice shortly.");
    setOpen(false); setStage("form"); setClientSecret(""); onPaid();
  };

  // Offline (record-and-verify): bank transfer, M-Pesa Paybill, or cash. Recorded
  // against the invoice and held pending until our team verifies it.
  const submit = async () => {
    if (isStripe) return startStripe();
    if (isMpesaGateway) return startMpesa();
    if (error || refMissing) return;
    setBusy(true);
    try {
      const { data } = await api.post("/v1/payments", { invoice_id: invoice.id, amount: Number(amount), method, provider: method, reference });
      const pending = data?.payment?.verify_status === "pending";
      toast.success(pending ? "Payment submitted — pending verification by our team." : "Payment recorded");
      setOpen(false); onPaid();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmDisabled = busy || (isOffline && (!!error || refMissing)) || phoneMissing;
  const confirmLabel = busy ? "Working…" : isStripe ? "Continue to card" : isMpesaGateway ? "Send M-Pesa prompt" : "Confirm payment";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button className="inline-flex items-center gap-1 text-xs text-[#d41f26] font-bold hover:underline" data-testid={`invoice-pay-${invoice.invoice_number}`}>
          <CreditCard size={12}/> Pay
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>{stage === "stripe" ? "Pay by card" : "Pay invoice"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="text-sm text-[#475569]">Invoice <span className="font-mono font-semibold text-[#0E2F76]">{invoice.invoice_number}</span></div>
          <div className="flex items-baseline justify-between">
            <div>
              <div className="vtx-eyebrow">Outstanding balance</div>
              <div className="text-2xl font-black text-[#0E2F76]" data-testid="pay-balance">{formatMoney(balance, invoice.currency)}</div>
            </div>
            <div className="text-right text-xs text-[#475569]">
              Invoice total<br />{formatMoney(invoice.amount, invoice.currency)}
            </div>
          </div>

          {stage === "stripe" ? (
            <StripeCheckout
              publishableKey={stripeMethod?.publishable_key}
              clientSecret={clientSecret}
              payLabel={`Pay ${formatMoney(balance, invoice.currency)}`}
              onSuccess={onStripeSuccess}
              onCancel={() => { setStage("form"); setClientSecret(""); }}
            />
          ) : (
            <>
              {isOffline && (
                <label className="block">
                  <span className="vtx-eyebrow block mb-1.5">Amount to pay</span>
                  <input type="number" step="0.01" min="0" max={balance} className="vtx-input font-mono" value={amount} onChange={(e)=>setAmount(e.target.value)} data-testid="pay-amount" />
                  {error && <div className="mt-1 text-xs text-[#d41f26] font-semibold" data-testid="pay-error">{error}</div>}
                  <button type="button" className="mt-1 text-[11px] text-[#0E2F76] hover:text-[#d41f26] font-semibold" onClick={()=>setAmount(String(balance))} data-testid="pay-full-balance">Pay full balance</button>
                </label>
              )}
              <label className="block">
                <span className="vtx-eyebrow block mb-1.5">Payment method</span>
                <select value={method} onChange={(e)=>{ setMethod(e.target.value); setReference(""); setPhone(""); }} className="vtx-input" data-testid="pay-method" disabled={methods === null}>
                  {list.map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              </label>
              {isStripe && (
                <div className="text-xs text-[#475569]">You'll enter your card next. The full outstanding balance is charged and settles automatically once Stripe confirms it.</div>
              )}
              {isMpesaGateway && (
                <>
                  <label className="block">
                    <span className="vtx-eyebrow block mb-1.5">M-Pesa phone number</span>
                    <input className="vtx-input font-mono" value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="2547XXXXXXXX" data-testid="pay-phone" />
                    {phoneMissing && <div className="mt-1 text-xs text-[#d41f26] font-semibold" data-testid="pay-phone-error">Enter the phone number to receive the M-Pesa prompt.</div>}
                  </label>
                  <div className="text-xs text-[#475569]">We'll send an STK push to this number for the full outstanding balance. Approve it on your phone; the invoice updates once M-Pesa confirms.</div>
                </>
              )}
              {isOffline && (
                <>
                  <label className="block">
                    <span className="vtx-eyebrow block mb-1.5">{sel.reference_label || "Reference"}</span>
                    <input className="vtx-input" value={reference} onChange={(e)=>setReference(e.target.value)} placeholder={sel.reference_label || "Reference"} data-testid="pay-reference" />
                    {refMissing && <div className="mt-1 text-xs text-[#d41f26] font-semibold" data-testid="pay-reference-error">A reference is required for {sel.label || "this method"}.</div>}
                  </label>
                  <div className="text-xs text-[#475569]">Partial payments are allowed; the balance updates until the invoice is settled.</div>
                  {sel.note && (
                    <div className="text-xs text-[#B45309]" data-testid="pay-method-note">{sel.note}</div>
                  )}
                </>
              )}
            </>
          )}
        </div>
        {stage !== "stripe" && (
          <DialogFooter>
            <button className="vtx-btn-ghost" onClick={()=>setOpen(false)}>Cancel</button>
            <button className="vtx-btn-primary" disabled={confirmDisabled} onClick={submit} data-testid="pay-confirm">
              {confirmLabel}
            </button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
